import React, { useEffect, useState } from 'react';
import { FaSave } from 'react-icons/fa';

const StatusTab = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState(null); // Data for Add/Edit modal
  const [deleteId, setDeleteId] = useState(null); // ID of status to delete
  const [editMode, setEditMode] = useState(false); // Edit mode toggle
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch statuses
  const fetchStatuses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/api/status`);
      if (!response.ok) throw new Error('Failed to fetch statuses');
      const data = await response.json();
      setStatuses(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  // Save or update a status
  const handleSave = async (status) => {
    const method = editMode ? 'PUT' : 'POST';
    const url = editMode
      ? `${apiUrl}/api/status/${status.id}`
      : `${apiUrl}/api/status`;

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(status),
      });
      if (!response.ok) throw new Error('Error saving status');
      await fetchStatuses();
      setModalData(null); // Close modal
    } catch (err) {
      setError('Error saving status. Please try again.');
    }
  };

  // Delete a status
  const handleDelete = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/status/${deleteId}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Error deleting status');
      await fetchStatuses();
      setDeleteId(null); // Close delete confirmation modal
    } catch (err) {
      setError('Error deleting status. Please try again.');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Statuses</h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditMode(false);
            setModalData({ name: '', description: '' }); // Open modal with empty data
          }}
        >
          Add Status
        </button>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : statuses.length === 0 ? (
        <p>No statuses available.</p>
      ) : (
        <table className="table w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 bg-base-200">Name</th>
              <th className="border border-gray-300 bg-base-200">Description</th>
              <th className="border border-gray-300 bg-base-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            {statuses.map((status) => (
              <tr key={status.id}>
                <td>{status.name}</td>
                <td>{status.description}</td>
                <td className="flex gap-2">
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => {
                      setEditMode(true);
                      setModalData(status); // Open modal with status data
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => setDeleteId(status.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add/Edit Modal */}
      {modalData && (
        <dialog className="modal modal-open bg-gray-50">
          <form
            className="modal-box"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave(modalData);
            }}
          >
            <h3 className="font-bold text-lg">
              {editMode ? 'Edit Status' : 'Add Status'}
            </h3>
            <div className="form-control">
              <label htmlFor="name" className="label-text">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={modalData.name || ''}
                onChange={(e) =>
                  setModalData((prev) => ({ ...prev, name: e.target.value }))
                }
                className="input input-bordered"
                required
              />
            </div>
            <div className="form-control">
              <label htmlFor="description" className="label-text">
                Description
              </label>
              <textarea
                name="description"
                value={modalData.description || ''}
                onChange={(e) =>
                  setModalData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="textarea textarea-bordered"
              />
            </div>
            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => setModalData(null)} // Close modal
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                <FaSave />
                {editMode ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirm Deletion</h3>
            <p>Are you sure you want to delete this status? This action cannot be undone.</p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setDeleteId(null)} // Close modal
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default StatusTab;
